class WishlistManager {
  static STORAGE_KEY = 'ilara-wishlist'

  static getItems() {
    try {
      return JSON.parse(localStorage.getItem(WishlistManager.STORAGE_KEY)) || []
    } catch {
      return []
    }
  }

  static setItems(items) {
    localStorage.setItem(WishlistManager.STORAGE_KEY, JSON.stringify(items))
  }

  static has(productId) {
    return WishlistManager.getItems().includes(Number(productId))
  }

  static toggle(productId) {
    const id = Number(productId)
    let items = WishlistManager.getItems()
    let action

    if (items.includes(id)) {
      items = items.filter((i) => i !== id)
      action = 'remove'
    } else {
      items.unshift(id)
      action = 'add'
    }

    WishlistManager.setItems(items)
    WishlistManager.syncAllButtons()
    WishlistManager.updateBadges()

    window.dispatchEvent(
      new CustomEvent('on:wishlist:change', {
        bubbles: true,
        detail: { action, productId: id, wishlist: items },
      })
    )

    return action
  }

  static syncAllButtons() {
    document.querySelectorAll('[data-wishlist-toggle]').forEach((el) => {
      const id = Number(el.dataset.productId)
      const isActive = WishlistManager.has(id)
      el.classList.toggle('wl-active', isActive)
      el.setAttribute('aria-pressed', String(isActive))
      el.setAttribute(
        'aria-label',
        isActive ? 'Quitar de favoritos' : 'Agregar a favoritos'
      )
    })
  }

  static updateBadges() {
    const count = WishlistManager.getItems().length
    document.querySelectorAll('.js-wishlist-count').forEach((badge) => {
      badge.textContent = count
      badge.hidden = count === 0
      badge.style.display = count === 0 ? 'none' : ''
      badge.classList.remove('wl-badge-bump')
      void badge.offsetWidth
      if (count > 0) badge.classList.add('wl-badge-bump')
    })
  }
}

if (!customElements.get('wishlist-toggle')) {
  customElements.define(
    'wishlist-toggle',
    class extends HTMLElement {
      connectedCallback() {
        this.productId = Number(this.dataset.productId)

        const btn =
          this.querySelector('[data-wishlist-toggle]') ||
          this.querySelector('.js-wishlist-button') ||
          this.querySelector('button')

        if (!btn) return
        this.btn = btn

        btn.setAttribute('data-wishlist-toggle', '')
        btn.dataset.productId = this.productId

        btn.addEventListener('click', (e) => {
          e.preventDefault()
          e.stopPropagation()
          const action = WishlistManager.toggle(this.productId)
          this._animateBump(action)
        })

        this.syncState()
      }

      syncState() {
        if (!this.btn) return
        const isActive = WishlistManager.has(this.productId)
        this.classList.toggle('wl-active', isActive)
        this.btn.classList.toggle('wl-active', isActive)
        this.btn.setAttribute('aria-pressed', String(isActive))
        this.btn.setAttribute(
          'aria-label',
          isActive ? 'Quitar de favoritos' : 'Agregar a favoritos'
        )
      }

      _animateBump(action) {
        if (action !== 'add') return
        const icon = this.btn.querySelector('svg')
        if (!icon) return
        icon.classList.remove('wl-bump')
        void icon.offsetWidth
        icon.classList.add('wl-bump')
        icon.addEventListener('animationend', () => icon.classList.remove('wl-bump'), { once: true })
      }
    }
  )
}

if (!customElements.get('wishlist-page')) {
  customElements.define(
    'wishlist-page',
    class extends HTMLElement {
      connectedCallback() {
        this.init()
        window.addEventListener('on:wishlist:change', () => this.init())
      }

      async init() {
        const items = WishlistManager.getItems()
        const emptyEl = this.querySelector('.js-wishlist-empty')
        const gridEl = this.querySelector('.js-wishlist-grid')
        const loaderEl = this.querySelector('.js-wishlist-loader')

        if (items.length === 0) {
          if (emptyEl) emptyEl.hidden = false
          if (gridEl) gridEl.innerHTML = ''
          if (loaderEl) loaderEl.hidden = true
          return
        }

        if (emptyEl) emptyEl.hidden = true
        if (loaderEl) loaderEl.hidden = false
        if (gridEl) gridEl.innerHTML = ''

        const query = items.map((id) => `id:${id}`).join(' OR ')
        const baseUrl = this.dataset.url
        const url = `${baseUrl}&q=${encodeURIComponent(query)}`

        try {
          const response = await fetch(url)
          if (!response.ok) throw new Error(response.status)

          const html = await response.text()

          const parser = new DOMParser()
          const doc = parser.parseFromString(html, 'text/html')

          const newGrid = doc.querySelector('.js-wishlist-grid')

          if (newGrid && gridEl) {
            gridEl.innerHTML = newGrid.innerHTML
            WishlistManager.syncAllButtons()
          } else if (gridEl) {
            const wlPage = doc.querySelector('wishlist-page')
            if (wlPage) {
              const fallbackGrid = wlPage.querySelector('.js-wishlist-grid')
              if (fallbackGrid) gridEl.innerHTML = fallbackGrid.innerHTML
            }
          }
        } catch (error) {
          console.error('Wishlist fetch error:', error)
        } finally {
          if (loaderEl) loaderEl.hidden = true
        }
      }
    }
  )
}

document.addEventListener('DOMContentLoaded', () => {
  WishlistManager.syncAllButtons()
  WishlistManager.updateBadges()
})

window.addEventListener('pageshow', (e) => {
  if (e.persisted) {
    WishlistManager.syncAllButtons()
    WishlistManager.updateBadges()
  }
})